import { createClient } from '@/lib/supabase/client';
import { Interview } from '@/lib/types/candidate';

const supabase = createClient();

export async function getSignedUrl(
  agentId: string | undefined
): Promise<string> {
  if (!agentId) throw Error('Agent ID is required');
  const response = await fetch(
    `/api/elevenlabs/signed-url?agent_id=${agentId}`
  );

  if (!response.ok) throw Error('Failed to get signed URL');

  const data = await response.json();
  return data.signed_url;
}

// Get conversation token for ElevenLabs API
export async function getConversationToken(
  agentId: string | undefined
): Promise<string> {
  if (!agentId) throw Error('Agent ID is required');

  const response = await fetch(
    `/api/elevenlabs/conversation-token?agent_id=${agentId}`
  );

  if (!response.ok) throw Error('Failed to get conversation token');

  const data = await response.json();
  return data.token;
}

// Optimized function to fetch multiple questions for a focus area
const getQuestionsForFocusArea = async (
  focus_area: string,
  tags: string[],
  count: number,
  excludeIds: Set<string> = new Set()
) => {
  // Define main framework tags to prioritize
  const frameworkTags = [
    'React',
    'Angular',
    'Vue',
    'Python',
    'Django',
    'Flask',
    'Laravel',
    'PHP'
  ];
  const interviewFramework = tags.find(tag => frameworkTags.includes(tag));

  let questions: any[] = [];

  // Step 1: Try to find framework-specific questions first
  if (interviewFramework) {
    const { data: frameworkData, error: frameworkError } = await supabase
      .from('interview_questions')
      .select('*')
      .eq('focus_area', focus_area)
      .overlaps('tags', tags)
      .contains('tags', [interviewFramework]);

    if (!frameworkError && frameworkData && frameworkData.length > 0) {
      // Filter out already used questions
      const availableQuestions = frameworkData.filter(q => !excludeIds.has(q.id.toString()));

      questions = availableQuestions;
    }
  }

  // Step 2: If we don't have enough framework-specific questions, add general questions
  if (questions.length < count) {
    const { data: generalData, error: generalError } = await supabase
      .from('interview_questions')
      .select('*')
      .eq('focus_area', focus_area)
      .overlaps('tags', tags);

    if (!generalError && generalData && generalData.length > 0) {
      // Filter out already used questions and framework-specific ones we already have
      const usedIds = new Set([...excludeIds, ...questions.map(q => q.id.toString())]);
      const availableGeneralQuestions = generalData.filter(q => !usedIds.has(q.id.toString()));
      
      // Add general questions to our collection
      questions = [...questions, ...availableGeneralQuestions];
    }
  }

  if (questions.length === 0) {
    throw new Error(`No questions for ${focus_area} found`);
  }

  // Shuffle and return the requested number of questions
  const shuffled = questions.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

// Fetch random interview questions based on interview focus areas
export async function getInterviewQuestions(interview: Interview): Promise<string[]> {
  // Get focus areas from interview
  const focusAreas = interview.focus_area || [
    'frontend',
    'backend'
  ];
  
  const tags = interview.tags || [];
  const usedQuestionIds = new Set<string>();

  try {
    // Fetch 1 question for each focus area
    const questions: string[] = [];
    
    for (const focusArea of focusAreas) {
      const focusAreaQuestions = await getQuestionsForFocusArea(
        focusArea,
        tags,
        1, // Get exactly 1 question per focus area
        usedQuestionIds
      );
      
      if (focusAreaQuestions.length === 0) {
        throw new Error(`No questions found for focus area: ${focusArea}`);
      }
      
      // Add the question to our collection
      questions.push(focusAreaQuestions[0].question);
      
      // Mark this question as used
      usedQuestionIds.add(focusAreaQuestions[0].id.toString());
    }

    // Return array of questions with variable length
    return questions;
  } catch (error) {
    console.error('Error fetching interview questions:', error);
    throw error;
  }
}
